<template>
  <div class="universal-login__body" :class="bodyClass">
    <form @submit.prevent="submitOrValidateRecaptcha" class="universal-login__form" :class="formClass">
      <vue-recaptcha
          v-if="recaptchaRequired"
          ref="invisibleRecaptcha"
          size="invisible"
          @verify="submitForm($event)"
          @error="recaptchaError = true"
          :sitekey="recaptchaSiteKey"
          :loadRecaptchaScript="true"
          :language="locale"
      >
      </vue-recaptcha>

      <p class="universal-login__title" :class="titleClass">{{ title }}</p>

      <div class="universal-login__input-button-wrap">
        <img :src="iconSrc" class="universal-login__input-icon" :class="inputIconClass" alt="" />


        <div class="universal-login__country-select input-group-prepend" v-if="oneProductSetup && !msisdnSubmitted">
          <button class="universal-login__country-select btn dropdown-toggle" type="button" data-toggle="dropdown"
                  aria-haspopup="true" aria-expanded="false">
            <span :class="getFlagClass(selectedCountry)"></span> {{ getCountryCode(selectedCountry) }}
          </button>
          <div class="dropdown-menu">

            <a v-for="(countryCode, country) in supportedCountries" @click="changeCountry(country)" :class="[country === selectedCountry ? ' selected' : '', 'dropdown-item']" href="#">
              <span :class="getFlagClass(country)"></span> {{ countryCode }}
            </a>
          </div>
        </div>

        <input class="universal-login__input" :class="inputClass" :id="inputId" :name="inputName" size="1"
               v-model.trim="inputs[msisdnSubmitted ? 'pin' : 'msisdn']" required
               :type="msisdnSubmitted ? 'number' : 'tel'"
        />

        <button type="submit" class="universal-login__button" :class="buttonClass">
          {{ translations.confirm }}
        </button>
      </div>
      <div v-if="isLoading || success" class="universal-login__loading" :class="loadingClass"></div>
      <div v-if="timeToStopSpinner" class="universal-login__holding">{{ translations.user_holding_message }}</div>

      <div v-if="success" class="universal-login__success"></div>
    </form>

    <div v-if="errorMessages" class="universal-login__error">
      <ul>
        <li v-for="message in errorMessages" v-html="message"></li>
      </ul>
    </div>

    <div v-if="subscribeLink" class="universal-login__subscribe">
      <a :href="subscribeLink" class="universal-login__subscribe-link"
         data-action_type="ClickedLink" data-action_target="subscribe-now">
        {{ translations.subscribe_now }}
      </a>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import VueRecaptcha from 'vue-recaptcha';

export default {
  name: 'universal-login-component',
  components: {VueRecaptcha},
  props: {
    recaptchaSiteKey: String,
    bypassRecaptcha: Boolean,
    recaptchaRequiredOnValidatePin: Boolean,
    translations: {
      validator(value) {
        const requiredKeys = ['confirm', 'pin_message', 'mobile_message', 'invalid_number', 'invalid_pin', 'general_error', 'exceeded_request_limit', 'subscribe_now', 'user_holding_message'];
        return requiredKeys.every(item => value.hasOwnProperty(item));
      }
    },
    requestPinUrl: String,
    msisdnInRequest: String,
    validatePinUrl: String,
    oneProductSetup: Boolean,
    supportedCountries: {},
    productCountry: String,
    modifier: {
      type: String,
      required: false,
      default: ""
    },
    locale: String,
    loggedInBefore: String
  },

  data() {
    return {
      isLoading: false,
      msisdnSubmitted: false,
      inputs: {
        msisdn: null,
        pin: null
      },
      errorMessages: null,
      success: false,
      subscribeLink: null,
      recaptchaError: false,
      pmcMixpanel: false,
      selectedCountry: '',
      timeToStopSpinner: null
    }
  },

  created() {
    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
    if (typeof window.PMCMixpanel !== 'undefined') {
      this.pmcMixpanel = true;
    }
  },
  mounted() {
    if (this.oneProductSetup) {
      this.selectedCountry = this.productCountry;
    }
    if (this.msisdnInRequest) {
      this.inputs.msisdn = this.msisdnInRequest;
      setTimeout(() => this.submitOrValidateRecaptcha(), 500)
    }
  },

  watch: {
    msisdn: function () {
      this.errorMessages = null
    },
    pin: function () {
      this.errorMessages = null
    }
  },

  computed: {
    /**
     * MSISDN and PIN are computed, so we can easily watch nested properties
     * They are nested properties so that dynamic v-model works correctly on input
     */
    msisdn() {
      return this.inputs.msisdn;
    },
    pin() {
      return this.inputs.pin;
    },

    title() {
      return this.msisdnSubmitted
          ? this.translations.pin_message
          : (
              this.loggedInBefore === '1'
                  ? this.translations.mobile_message
                  : this.translations.invalid_number
          );
    },

    inputId() {
      return this.msisdnSubmitted ? 'pin-input' : 'msisdn-input';
    },

    inputName() {
      return this.msisdnSubmitted ? 'pin' : 'msisdn';
    },

    titleClass() {
      return this.withModifier(
          {
            'universal-login__title--msisdn': !this.msisdnSubmitted,
            'universal-login__title--pin': this.msisdnSubmitted
          },
          'universal-login__title'
      );
    },

    inputClass() {
      return this.withModifier(
          {
            'universal-login__input--msisdn': !this.msisdnSubmitted,
            'universal-login__input--pin': this.msisdnSubmitted
          },
          'universal-login__input'
      );
    },

    inputIconClass() {
      return this.withModifier(
          {
            'universal-login__input-icon--msisdn': !this.msisdnSubmitted,
            'universal-login__input-icon--pin': this.msisdnSubmitted
          },
          'universal-login__input-icon'
      );
    },

    iconSrc() {
      return this.msisdnSubmitted ? '/vendor/universal-login/images/padlock.svg' : '/vendor/universal-login/images/phone.svg';
    },

    bodyClass() {
      return this.withModifier({}, 'universal-login__body');
    },

    buttonClass() {
      return this.withModifier(
          {
            'universal-login__button--msisdn': !this.msisdnSubmitted,
            'universal-login__button--pin': this.msisdnSubmitted
          },
          'universal-login__button'
      );
    },

    formClass() {
      return this.withModifier(
          {
            'universal-login__form--covered': this.isLoading || this.success
          },
          'universal-login__form'
      );
    },

    loadingClass() {
      return this.withModifier(
          {
            'universal-login__loading--success': this.success
          },
          'universal-login__loading'
      );
    },

    recaptchaRequired() {
      return !this.bypassRecaptcha && !this.recaptchaError && this.recaptchaSiteKey && ( !this.msisdnSubmitted || this.recaptchaRequiredOnValidatePin )
    },

    postData() {
      return this.bypassRecaptcha ? {'skip_recaptcha': '1'} : {};
    }
  },

  methods: {
    submitMsisdn(recaptchaResponse) {
      if (this.msisdn === null || this.msisdn === '') {
        this.errorMessages = [this.translations.invalid_number];
        this.resetRecaptcha();
        return null
      }
      this.isLoading = true;

      axios
          .post(this.requestPinUrl, Object.assign(this.postData, {
            msisdn: this.msisdn,
            country: this.selectedCountry ? this.selectedCountry : this.productCountry,
            'g-recaptcha-response': recaptchaResponse
          }))
          .then((response) => {
            if (typeof response.data.redirect !== 'undefined') {
              console.log(response.data.redirect)
              window.location.href = response.data.redirect
            }
            if (response.data.status === 'ok') {
              this.msisdnSubmitted = true;
            }
            this.isLoading = false
            this.timeToStopSpinner = null  //Need to set to null to reset timer
            this.resetRecaptcha();
          })
          .catch((error) => {
            if (typeof error.response.data.holdMinutes !== 'undefined') {
              this.handleHoldingUser(error, recaptchaResponse)
              return
            }
            this.isLoading = false
            this.resetRecaptcha();
            this.handleError(error)
          })
    },

    /**
     * @returns {boolean}
     */
    pinIsValid() {
      const valid4DigitNumber = /^\d{4}$/;
      if (this.pin === null || this.pin === '' || !valid4DigitNumber.test(this.pin)) {
        this.errorMessages = [this.translations.invalid_pin];
        return false;
      }
      return true;
    },

    /**
     * @param {String|null} recaptchaResponse
     */
    checkPin(recaptchaResponse) {
      if (this.pinIsValid()) {
        this.isLoading = true;
        axios
            .post(this.validatePinUrl, Object.assign(this.postData, {
              pin: this.pin,
              'g-recaptcha-response': recaptchaResponse
            }))
            .then((response) => {
              this.success = true;
              window.location.href = response.data.redirectUrl;
            })
            .catch((error) => {
              this.handleError(error)
            })
            .finally(() => {
              this.isLoading = false;
            });
      }
      this.resetRecaptcha();
    },

    submitOrValidateRecaptcha() {
      this.errorMessages = null;
      if (this.recaptchaRequired) {
        this.$refs.invisibleRecaptcha.execute();
      } else {
        this.submitForm(null);
      }
    },

    submitForm(recaptchaResponse) {
      if (this.pmcMixpanel) {
        PMCMixpanel.track(PMCMixpanel.triggeredAction, {
          action_type: PMCMixpanel.triggeredActionEvents.LOGIN_FORM_SUBMITTED,
          action_target: this.msisdnSubmitted ? 'VerifyPin' : 'RequestPin'
        });
      }
      if (!this.msisdnSubmitted) {
        this.submitMsisdn(recaptchaResponse);
      } else {
        this.checkPin(recaptchaResponse);
      }
    },

    resetRecaptcha() {
      if (this.recaptchaRequired) {
        this.$refs.invisibleRecaptcha.reset()
      }
    },

    handleError(error) {
      this.errorMessages = [];
      this.subscribeLink = typeof error.response.data.subscribeLink !== 'undefined' ? error.response.data.subscribeLink : null;
      for (const key in error.response.data.errors) {
        error.response.data.errors[key].forEach(message => this.errorMessages.push(message));
      }
      if (error.response.status === 429) { // Too many requests
        this.errorMessages.push(this.translations.exceeded_request_limit);
      } else if (this.errorMessages.length === 0) {
        this.errorMessages.push(this.translations.general_error);
      }
    },

    // Hold a user for a certain time to keep retrying logging them in for delays in their subscription
    // See https://pmcdesk.atlassian.net/browse/PKB-776
    handleHoldingUser(error, recaptchaResponse) {
      // If we have holdTime in the response then add a time in future to stop spinner if it's not already set
      if (! this.timeToStopSpinner) {
        console.log("User may have delay in subscription, holding and retrying...")
        // timeToStopSpinner is set to a time int of time + holdMinutes in the future
        this.timeToStopSpinner = typeof error.response.data.holdMinutes !== 'undefined'
            ? new Date().getTime() + error.response.data.holdMinutes * 60000
            : null;
      }

      // If there is a timeToSpinner set, check if it is in the future, if so
      // try the msisdn every 10 seconds until the time is in the past
      if (this.timeToStopSpinner && new Date().getTime() <= this.timeToStopSpinner) {
        // Resubmit msisdn after 10 seconds
        setTimeout(() => {
          this.submitMsisdn(recaptchaResponse);
        }, 10000);
      } else {
        // Remove holding of the user as the timeToStopSpinner is in the past
        this.timeToStopSpinner = null  //Need to set to null to reset timer
        this.resetRecaptcha();
        this.isLoading = false
        this.handleError(error);
      }
    },

    withModifier(classes, baseClass) {
      const modifierClass = baseClass + '--' + this.modifier;
      classes[modifierClass] = this.modifier.length > 0;
      return classes;
    },

    changeCountry(country) {
      this.selectedCountry = country;
    },

    getCountryCode(country) {
      return this.supportedCountries[country.toUpperCase()]
    },

    getFlagClass(country) {
      return 'fi fi-' + country.toLowerCase();
    }
  }
}
</script>
